import { Desc, VariableMap } from '@sm/intl';

import {
  CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY,
  COVID_NEAR_YOU_COPY,
  CX_ENDPAGE_COPY,
  GIANCARLO_ENDPAGE_COPY,
  GO_BEYOND_TEXT_BANNER_COPY,
  MRX_ENDPAGE_COPY,
  RACIAL_JUSTICE_ENDPAGE_COPY,
  POLLING_ENDPAGE_COPY,
  POLLING_TEXT_BANNER_COPY,
  STANDARD_ENDPAGE_DEFAULT_COPY,
  UPENN_POLLING_ENDPAGE_COPY,
} from './copy';

import AustraliaMap from '~static/images/australia_polling.png';
import CanadaMap from '~static/images/canada_map.png';
import covidNearYou from '~static/images/covid-near-you-logo.svg';
import cxMaze from '~static/images/cx-maze.png';
import giancarlo from '~static/images/giancarlo.png';
import mrxShoe from '~static/images/mrx-shoe.png';
import researchReport from '~static/images/research-report.png';
import rjGroup from '~static/images/rj-group.png';
import ScotlandMap from '~static/images/scotland_polling.png';
import UKMap from '~static/images/uk_election_map_large.png';
import USMap from '~static/images/us_political_map_large.webp';
import { BackgroundColorProp } from '~app/components/TextBanner';
import { ShadedBackgroundColorProp } from '~app/components/EndpageImage';
import { getSrc } from '~app/helpers/assetLoading';

type PageProps = {
  imageSource: string;
  logoRoute: string;
  pageCTA: Desc;
  pageCTARoute?: string;
  pageHeading: Desc;
  pageSubheading?: Desc;
  privacyNotice?: [Desc, VariableMap];
  shadedBackgroundColor?: ShadedBackgroundColorProp;
  textBannerBackgroundColor: BackgroundColorProp;
  textBannerCTA: Desc;
  textBannerCTARoute: string;
  textBannerHeading?: Desc;
  textBannerSubheading?: Desc;
  utSource2: string;
};

export type Page = {
  [key: string]: PageProps;
};

const Pages: Page = {
  CorporateSignatureResearchEndpage: {
    imageSource: getSrc(researchReport),
    logoRoute: '/',
    pageCTA: CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY.PAGE_CTA_TEXT,
    // @todo https://momentiveai.atlassian.net/browse/ANONWEB-5440
    // Before endpages are exposed to non-english locales we will need to localize URLs to some CMS website, see ticket.
    pageCTARoute: '/mp/2020-research-report/',
    pageHeading: CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY.PAGE_SUBHEADING,
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'CorporateSignatureResearchEndpage',
  },
  CovidNearYouEndpage: {
    imageSource: getSrc(covidNearYou),
    logoRoute: '/',
    pageCTA: COVID_NEAR_YOU_COPY.PAGE_CTA_TEXT,
    pageHeading: COVID_NEAR_YOU_COPY.PAGE_HEADING,
    pageSubheading: COVID_NEAR_YOU_COPY.PAGE_SUBHEADING,
    privacyNotice: [
      COVID_NEAR_YOU_COPY.PRIVACY_NOTICE,
      {
        SurveyMonkeyPrivacyPolicyLink: 'https://www.surveymonkey.com/mp/legal/privacy/',
      },
    ],
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'CovidNearYouEndpage',
  },
  CxEndpage: {
    imageSource: getSrc(cxMaze),
    logoRoute: '/',
    pageCTA: CX_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageCTARoute: 'https://www.getfeedback.com/en/start/',
    pageHeading: CX_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: CX_ENDPAGE_COPY.PAGE_SUBHEADING,
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'CxEndpage',
  },
  GiancarloEndpage: {
    imageSource: getSrc(giancarlo),
    logoRoute: '/',
    pageCTA: GIANCARLO_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageCTARoute: '/mp/ask/',
    pageHeading: GIANCARLO_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: GIANCARLO_ENDPAGE_COPY.PAGE_SUBHEADING,
    shadedBackgroundColor: 'dark3',
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GIANCARLO_ENDPAGE_COPY.TEXT_BANNER_CTA_TEXT,
    textBannerCTARoute: '/mp/ask/',
    textBannerHeading: GIANCARLO_ENDPAGE_COPY.TEXT_BANNER_HEADING,
    utSource2: 'GiancarloEndpage',
  },
  MrxEndpage: {
    imageSource: getSrc(mrxShoe),
    logoRoute: '/',
    pageCTA: MRX_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageCTARoute: '/curiosity/how-allbirds-uses-surveymonkey-audience-to-fuel-its-explosive-brand-growth/',
    pageHeading: MRX_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: MRX_ENDPAGE_COPY.PAGE_SUBHEADING,
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'MrxEndpage',
  },
  PollingAustraliaMap: {
    imageSource: getSrc(AustraliaMap),
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      // @todo: https://momentiveai.atlassian.net/browse/ANONWEB-5440
      // Before endpages are exposed to non-english locales we will need to localize URLs to some CMS website, see ticket.
      // @todo: add ut_source tracking
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingAustraliaMap',
  },
  PollingCanadaMap: {
    imageSource: getSrc(CanadaMap),
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingCanadaMap',
  },
  PollingScotlandMap: {
    imageSource: getSrc(ScotlandMap),
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingScotlandMap',
  },
  PollingUKMap: {
    imageSource: getSrc(UKMap),
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingUKMap',
  },
  PollingUPenn: {
    imageSource: getSrc(USMap),
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      UPENN_POLLING_ENDPAGE_COPY.PRIVACY_NOTICE,
      {
        UPennPrivacyPolicyLink: 'https://www.upenn.edu/about/privacy-policy',
        SurveyMonkeyPrivacyPolicyLink: 'https://www.surveymonkey.com/mp/legal/privacy/',
      },
    ],
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingUPenn',
  },
  PollingUSMap: {
    imageSource: getSrc(USMap),
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingUSMap',
  },
  RacialJusticeEndpage: {
    imageSource: getSrc(rjGroup),
    logoRoute: '/',
    pageCTA: RACIAL_JUSTICE_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageCTARoute: '/mp/workforce-racial-equity/',
    pageHeading: RACIAL_JUSTICE_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: RACIAL_JUSTICE_ENDPAGE_COPY.PAGE_SUBHEADING,
    shadedBackgroundColor: 'light1',
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'RacialJusticeEndpage',
  },
};

export default Pages;
